import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { createRazorpayOptions } from '../../../config/razorpay';

const BookingSummary = ({
  formData,
  selectedServices,
  selectedDate,
  selectedTime,
  selectedLocation,
  preferences,
  onSubmit,
  isSubmitting
}) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // --- OLD SERVICES (Commented) ---
  /*
  const servicesOld = [
    {
      id: 'wash-fold',
      name: 'Wash & Fold',
      price: 149,
      unit: 'per kg'
    },
    {
      id: 'express-wash',
      name: 'Express Wash',
      price: 199,
      unit: 'per kg'
    },
    {
      id: 'dry-cleaning',
      name: 'Dry Cleaning',
      price: 299,
      unit: 'per piece'
    },
    {
      id: 'premium-pressing',
      name: 'Premium Pressing',
      price: 99,
      unit: 'per piece'
    },
    {
      id: 'delicate-wash',
      name: 'Delicate Wash',
      price: 249,
      unit: 'per piece'
    },
    {
      id: 'eco-wash',
      name: 'Eco-Friendly Wash',
      price: 179,
      unit: 'per kg'
    },
    {
      id: 'curtain-cleaning',
      name: 'Curtain & Drape Cleaning',
      price: 399,
      unit: 'per piece'
    },
    {
      id: 'pickup-delivery',
      name: 'Pickup & Delivery',
      price: 0,
      unit: 'free service'
    }
  ];
  */
  // --- END OLD SERVICES ---

  // New 9 Services (from services-overview)
  const services = [
    {
      id: 'dry-cleaning',
      name: 'Dry Cleaning',
      price: 90,
      unit: '/ Item'
    },
    {
      id: 'laundry-wash-fold',
      name: 'Laundry Wash & Fold',
      price: 119,
      unit: '/ Kg'
    },
    {
      id: 'laundry-wash-iron',
      name: 'Laundry Wash & Iron',
      price: 149,
      unit: '/ Kg'
    },
    {
      id: 'steam-press',
      name: 'Steam Press',
      price: 20,
      unit: '/ Item'
    },
    {
      id: 'toys-cleaning',
      name: 'Toys Cleaning',
      price: 120,
      unit: '/ Item'
    },
    {
      id: 'shoe-care',
      name: 'Shoe Care',
      price: 290,
      unit: '/ Item'
    },
    {
      id: 'leather-care',
      name: 'Leather Care',
      price: 150,
      unit: '/ Item'
    },
    {
      id: 'carpet-cleaning',
      name: 'Carpet Cleaning',
      price: 40,
      unit: '/ Sq ft'
    },
    {
      id: 'curtain-cleaning',
      name: 'Curtain Cleaning',
      price: 150,
      unit: '/ Kg'
    },
  ];

  const locations = [
    { value: 'rs-puram', label: 'RS Puram' },
    { value: 'gandhipuram', label: 'Gandhipuram' },
    { value: 'peelamedu', label: 'Peelamedu' },
    { value: 'saibaba-colony', label: 'Saibaba Colony' },
    { value: 'race-course', label: 'Race Course' },
    { value: 'singanallur', label: 'Singanallur' }
  ];

  const timeSlots = [
    { id: '09:00', label: '9:00 AM - 11:00 AM' },
    { id: '11:00', label: '11:00 AM - 1:00 PM' },
    { id: '13:00', label: '1:00 PM - 2:00 PM (Lunch)' },
    { id: '14:00', label: '2:00 PM - 4:00 PM' },
    { id: '16:00', label: '4:00 PM - 6:00 PM' },
    { id: '18:00', label: '6:00 PM - 8:00 PM' },
    { id: '20:00', label: '8:00 PM - 10:00 PM' }
  ];

  const preferenceOptions = [
    { id: 'eco-friendly', label: 'Eco-Friendly Detergent' },
    { id: 'fabric-softener', label: 'Extra Fabric Softener' },
    { id: 'starch', label: 'Light Starch' },
    { id: 'fragrance-free', label: 'Fragrance-Free' },
    { id: 'separate-colors', label: 'Separate Dark & Light Colors' },
    { id: 'express-service', label: 'Express Service (24 Hours)' }
  ];

  const getSelectedServices = () => {
    return services?.filter(service => selectedServices?.includes(service?.id));
  };

  const getSelectedLocation = () => {
    return locations?.find(loc => loc?.value === selectedLocation)?.label || '';
  };

  const getSelectedTimeSlot = () => {
    return timeSlots?.find(slot => slot?.id === selectedTime)?.label || '';
  };

  const getSelectedPreferences = () => {
    return preferenceOptions?.filter(pref => preferences?.includes(pref?.id));
  };

  const calculateTotal = () => {
    const serviceTotal = getSelectedServices()?.reduce((total, service) => total + service?.price, 0);
    const expressCharge = preferences?.includes('express-service') ? 50 : 0;
    return serviceTotal + expressCharge;
  };

  const handleSubmitClick = async () => {
    if (!isFormValid()) return;

    // Call the original onSubmit function passed down from parent
    // The parent has `onSubmit={handleSubmit}` which triggers handleBookingProcess
    await onSubmit();
  };

  const isFormValid = () => {
    return (
      formData?.name &&
      formData?.mobile &&
      selectedDate &&
      selectedTime
      // selectedServices?.length > 0 && // hidden (step 2 commented out)
      // selectedLocation &&              // hidden (step 4 commented out)
      // formData?.address &&             // optional field
      // formData?.pincode &&             // optional field
      // formData?.area                   // optional field
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 sticky top-6">
      <div className="space-y-6">
        <div className="border-b border-border pb-4">
          <h3 className="text-lg font-semibold text-foreground mb-2">Booking Summary</h3>
          <p className="text-sm text-muted-foreground">Review your pickup details before confirming</p>
        </div>

        {/* Customer Details */}
        <div className="space-y-3">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="User" size={16} />
            <span>Customer Details</span>
          </h4>
          <div className="text-sm text-muted-foreground space-y-1 pl-6">
            <p><span className="font-medium">Name:</span> {formData?.name || 'Not provided'}</p>
            <p><span className="font-medium">Mobile:</span> {formData?.mobile || 'Not provided'}</p>
            <p><span className="font-medium">Address:</span> {formData?.address || 'Not provided'}</p>
            <p><span className="font-medium">Area:</span> {formData?.area || 'Not provided'} - {formData?.pincode || 'Not provided'}</p>
          </div>
        </div>

        {/* Selected Services - hidden (step 2 commented out), re-enable to restore */}
        {/* <div className="space-y-3">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Package" size={16} />
            <span>Selected Services</span>
          </h4>
          <div className="space-y-1 pl-6">
            {getSelectedServices()?.length > 0 ? (
              getSelectedServices()?.map((service) => (
                <p key={service?.id} className="text-sm text-muted-foreground">
                  • {service?.name} ({service?.unit})
                </p>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No services selected</p>
            )}
          </div>
        </div> */}

        {/* Pickup Schedule */}
        <div className="space-y-3">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Calendar" size={16} />
            <span>Pickup Schedule</span>
          </h4>
          <div className="text-sm text-muted-foreground space-y-1 pl-6">
            <p><span className="font-medium">Date:</span> {selectedDate || 'Not selected'}</p>
            <p><span className="font-medium">Time:</span> {getSelectedTimeSlot() || 'Not selected'}</p>
            {/* Location - hidden (step 4 commented out), re-enable to restore */}
            {/* <p><span className="font-medium">Location:</span> {getSelectedLocation() || 'Not selected'}</p> */}
          </div>
        </div>

        {/* Preferences */}
        {getSelectedPreferences()?.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-foreground flex items-center space-x-2">
              <Icon name="Settings" size={16} />
              <span>Preferences</span>
            </h4>
            <div className="space-y-1 pl-6">
              {getSelectedPreferences()?.map((pref) => (
                <p key={pref?.id} className="text-sm text-muted-foreground">• {pref?.label}</p>
              ))}
            </div>
          </div>
        )}

        {/* Total Breakdown */}
        <div className="border-t border-border pt-4">
          {/* Services & total breakdown - hidden (step 2 commented out), re-enable to restore */}
          {/* <div className="space-y-2 mb-4">
            {getSelectedServices()?.map((service) => (
              <div key={service?.id} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{service?.name}</span>
                <span className="font-medium text-foreground">
                  {service?.price === 0 ? 'Free' : `₹${service?.price}`}
                </span>
              </div>
            ))}
            {preferences?.includes('express-service') && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Express Service (24 Hours)</span>
                <span className="font-medium text-foreground">₹50</span>
              </div>
            )}
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-foreground">Total Amount:</span>
                <span className="text-xl font-bold text-primary">₹{calculateTotal()}</span>
              </div>
            </div>
          </div> */}

          <Button
            variant="default"
            fullWidth
            size="lg"
            onClick={handleSubmitClick}
            disabled={!isFormValid() || isSubmitting || isProcessingPayment}
            loading={isSubmitting || isProcessingPayment}
            iconName="Calendar"
            iconPosition="left"
            className="bg-primary hover:bg-primary/90"
          >
            {/* OLD: {isProcessingPayment ? 'Processing Payment...' : isSubmitting ? 'Scheduling...' : 'Pay & Schedule Pickup'} */}
            {isSubmitting ? 'Scheduling...' : 'Schedule Pickup'}
          </Button>

          <div className="mt-3 space-y-1">
            <p className="text-xs text-muted-foreground text-center">
              *Final pricing may vary based on actual quantity and services
            </p>
            {/* Payment note - hidden, re-enable when Razorpay is active */}
            {/* <p className="text-xs text-muted-foreground text-center">
              *Payment is processed securely via Razorpay
            </p> */}
          </div>
        </div>

        {!isFormValid() && (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">Complete Required Fields</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Please fill all required fields to schedule your pickup
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSummary;
